import { memoize } from 'lodash-es';
import { getTenantNamespace } from './tenant.js';

// eslint-disable-next-line no-restricted-imports
/**
 * Gets a boot flag value which is basically a feature flag value, but is available when the application starts (i.e.
 * before the user has authenticated). Function is memoized so it only tries to resolve a given flag once.
 */ const getBootFlag = memoize(_getBootFlag);
function _getBootFlag(flag) {
    try {
        // We're using `parent` here instead of `window` so that if/when this is used inside the Grafana iframe, it'll use
        // the values loaded by Cloud UI (when used in Cloud, `parent` should just resolve to the regular `window`)
        const windowValues = parent.chronoBootFlags;
        if (!isJsObject(windowValues)) {
            throw new Error(`Expected a JavaScript object for BootFlags but received '${windowValues}'`);
        }
        if (!(flag.key in windowValues)) {
            throw new Error(`Key '${flag.key}' is missing from BootFlag values`);
        }
        const valueFromWindow = windowValues[flag.key];
        if (!flag.isValid(valueFromWindow)) {
            throw new Error(`Invalid value '${valueFromWindow}' for flag '${flag.key}'`);
        }
        return valueFromWindow;
    } catch (err) {
        const fallbackValue = typeof flag.defaultValue === 'function' ? flag.defaultValue() : flag.defaultValue;
        console.error(`Using default value '${fallbackValue}' for BootFlag '${flag.key}'`, err);
        return fallbackValue;
    }
}
// The default for Sentry is disabled in RC environments (wrapped in a function to avoid evaluating this until needed)
function sentryDefault() {
    const isRcEnvironment = getTenantNamespace().startsWith('rc-');
    return isRcEnvironment ? 'disabled' : 'enabled';
}
/**
 * A subset of feature flag values available when the application starts.
 */ const BootFlags = {
    /**
   * Controlled by the `fullstory-ui-mode` flag in LD.
   */ FULL_STORY_MODE: stringUnionFlag('fullStoryMode', 'disabled', [
        'enabled',
        'disabled',
        'relayed'
    ]),
    /**
   * Controlled by the `sentry-ui-mode` flag in LD.
   */ SENTRY_MODE: stringUnionFlag('sentryMode', sentryDefault, [
        'enabled',
        'disabled'
    ]),
    /**
   * Controlled by the `doorbell-ui-mode` flag in LD.
   */ DOORBELL_MODE: stringUnionFlag('doorBellMode', 'disabled', [
        'internal',
        'external',
        'disabled'
    ]),
    /**
   * Controlled by the `ui-network-monitoring-mode` flag in LD.
   */ NETWORK_MONITORING_ENABLED: stringUnionFlag('networkMonitoringEnabled', 'false', [
        'true',
        'false'
    ]),
    /**
   * Controlled by the `userguiding-ui-mode` flag in LD.
   */ USERGUIDING_MODE: stringUnionFlag('userGuidingMode', 'disabled', [
        'enabled',
        'disabled'
    ]),
    /**
   * Controlled by the `chameleon-ui-mode` flag in LD. Experimental potential replacement for
   * user guiding.
   */ CHAMELEON_MODE: stringUnionFlag('chameleonMode', 'disabled', [
        'enabled',
        'disabled'
    ])
};
// Helper to define a boot flag that has a string union value
function stringUnionFlag(key, defaultValue, validValues) {
    return {
        key,
        defaultValue,
        isValid: (windowValue)=>{
            // Loop through the valid values and if the windowValue is found, it's valid
            for (const valid of validValues){
                if (windowValue === valid) return true;
            }
            return false;
        }
    };
}
// Helper to check for a JavaScript object { ... } with string props
function isJsObject(val) {
    return typeof val === 'object' && val !== null && Array.isArray(val) === false;
}

export { BootFlags, getBootFlag, stringUnionFlag };
//# sourceMappingURL=boot-flags.js.map
